import { useEffect, type RefObject } from "react";
import { useLockStore } from "../../store/lockStore";
import { lockApi } from "../../api/lockApi";
import { asError, eventBus } from "@optimyl/events";

/**
 * Acquire the lock on mount, release on unmount, and register the
 * `beforeunload` beacon that releases on tab close. The beacon is best-effort
 * (Safari ITP drops it on cross-origin nav); the 90s server-side TTL is the
 * real safety net for crashes.
 */
export function useLockLifecycle(
  enabled: boolean,
  formId: string | null,
  sessionId: string,
  formIdRef: RefObject<string | null>,
  sessionIdRef: RefObject<string>,
): void {
  const setLockStatus = useLockStore((s) => s.setLockStatus);
  const setRequestInProgress = useLockStore((s) => s.setRequestInProgress);
  const resetTransient = useLockStore((s) => s.resetTransient);

  useEffect(() => {
    if (!enabled || !formId) return;
    let cancelled = false;

    (async () => {
      try {
        const result = await lockApi.acquireLock(formId, sessionId);
        if (cancelled) return;

        if (result.acquired) {
          setLockStatus("locked");
          setRequestInProgress(result.requestAccessBy);
        } else {
          setLockStatus("locked-by-other", result.lockedBy);
          setRequestInProgress(result.requestAccessBy);
        }
      } catch (err) {
        eventBus.publish("error", { error: asError(err), context: "useLockLifecycle.acquire" });
      }
    })();

    // sendBeacon release on tab close — best effort. The 90s server-side
    // heartbeat expiry is the real safety net if beacon fails.
    const onBeforeUnload = () => {
      if (formIdRef.current && sessionIdRef.current) {
        lockApi.releaseLockBeacon(formIdRef.current, sessionIdRef.current);
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      cancelled = true;
      window.removeEventListener("beforeunload", onBeforeUnload);
      // Normal unmount path — fire-and-forget release. Server treats a session
      // mismatch as a no-op so a stale unmount can't break someone else's lock.
      if (formId) {
        lockApi.releaseLock(formId, sessionId).catch(() => { /* nothing useful to do */ });
      }
      resetTransient();
    };
  }, [enabled, formId, sessionId, setLockStatus, setRequestInProgress, resetTransient, formIdRef, sessionIdRef]);
}
